import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography, Divider } from '@mui/material';
import { useHistory } from 'react-router-dom';
import notificationAPI from '../services/notificationAPI';
import { formatAmountForDisplay } from '../utils/amountFormatter';

const NotificationPanel = ({ isOpen, onClose, onUnreadCountChange }) => {
  const history = useHistory();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [panelStyle, setPanelStyle] = useState({});
  const [isPositioned, setIsPositioned] = useState(false);
  const panelRef = useRef(null);

  // Dynamic positioning based on notification button
  useEffect(() => {
    if (isOpen) {
      const updatePosition = () => {
        const notificationButton = document.querySelector('.notification-trigger');
        if (notificationButton) {
          const buttonRect = notificationButton.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          
          // Calculate panel position dynamically
          const panelWidth = Math.min(450, viewportWidth * 0.9);
          
          // Center the panel under the button
          const buttonCenterX = buttonRect.left + (buttonRect.width / 2);
          const panelLeft = buttonCenterX - (panelWidth / 2);
          
          
          // Get actual triangle height from the SVG element
          const triangleElement = panelRef.current?.querySelector('svg');
          const triangleHeight = triangleElement ? triangleElement.getBoundingClientRect().height : 12;
          
          setPanelStyle({
            position: 'fixed',
            top: `${buttonRect.bottom + triangleHeight}px`, // Panel below circle, triangle reaches up
            left: `${panelLeft}px`, // Direct calculation: button center - half panel width
            width: `${panelWidth}px`,
            zIndex: 1000
          });
          
          setIsPositioned(true);
        }
      };

      // Calculate position with proper timing for layout stability
      const timeoutId = setTimeout(() => {
        requestAnimationFrame(updatePosition);
      }, 0);
      
      // Additional update after DOM fully settles for triangle height
      const delayedTimeoutId = setTimeout(() => {
        requestAnimationFrame(updatePosition);
      }, 10);
      
      // Update position on window resize
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);
      
      return () => {
        clearTimeout(timeoutId);
        clearTimeout(delayedTimeoutId);
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition);
      };
    } else {
      // Reset positioning state when panel closes
      setIsPositioned(false);
      setPanelStyle({});
    }
  }, [isOpen]);

  // Fetch notifications
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await notificationAPI.getNotifications({ size: 40 });
      setNotifications(response.content || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      await notificationAPI.getUnreadCount();
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Mark notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationAPI.markAsRead(notificationId);
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, readAt: new Date().toISOString() }
            : notif
        )
      );
      // Notify parent component of unread count change
      if (onUnreadCountChange) {
        onUnreadCountChange(0); // We don't track count here anymore
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      // Update local state
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, readAt: new Date().toISOString() }))
      );
      // Notify parent component of unread count change
      if (onUnreadCountChange) {
        onUnreadCountChange(0); // We don't track count here anymore
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.readAt) {
      await handleMarkAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.withdrawalRequest) {
      // Navigate to Requests page
      history.push('/requests');
      onClose(); // Close the notification panel
    } else if (notification.transaction) {
      // Navigate to transactions list first, then to specific transaction
      // This ensures that when user closes the details page, they go back to transactions list
      history.push('/transactions');
      // Small delay to ensure the transactions page loads before navigating to details
      setTimeout(() => {
        history.push(`/transactions/${notification.transaction.id}`);
      }, 100);
      onClose(); // Close the notification panel
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  // Get notification type and amount
  const getNotificationDetails = (notification) => {
    if (notification.withdrawalRequest) {
      return {
        type: 'Demande de retrait',
        amount: formatAmountForDisplay(notification.withdrawalRequest.amount, false).text
      };
    } else if (notification.transaction) {
      return {
        type: notification.transaction.category || 'Transaction',
        amount: formatAmountForDisplay(notification.transaction.amount, false).text
      };
    } else if (notification.type === 'COMMENT_ADDED') {
      return {
        type: 'Commentaire',
        amount: ''
      };
    }
    return { type: 'Notification', amount: '' };
  };

  // Format amounts and highlight important information in notification messages
  const parseAndFormatAmounts = (message) => {
    if (!message) return 'Nouvelle notification';
    
    let formattedMessage = message;
    
    // 1. Format amounts with proper thousands separators and make them bold
    const amountRegex = /(\d+(?:[.,]\d+)*)\s*MAD/g;
    formattedMessage = formattedMessage.replace(amountRegex, (match, amountStr) => {
      try {
        let cleanAmount = amountStr;
        
        if (cleanAmount.includes('.') && cleanAmount.includes(',')) {
          cleanAmount = cleanAmount.replace(/\./g, '').replace(',', '.');
        } else if (cleanAmount.includes(',')) {
          cleanAmount = cleanAmount.replace(',', '.');
        } else if (cleanAmount.includes('.')) {
          const parts = cleanAmount.split('.');
          if (parts.length === 2 && parts[1].length <= 2) {
            // Decimal
          } else {
            // Thousands separator
            cleanAmount = cleanAmount.replace(/\./g, '');
          }
        }
        
        const numericAmount = parseFloat(cleanAmount);
        if (isNaN(numericAmount)) return match;
        
        const formatted = formatAmountForDisplay(numericAmount, false).text;
        return `<strong>${formatted}</strong>`;
      } catch (error) {
        return match;
      }
    });
    
    // 2. Make category names bold (between "transaction" and "de {amount}")
    formattedMessage = formattedMessage.replace(/Une transaction ([A-Za-zÀ-ÿ\s]+) de <strong>/g, 'Une transaction <strong>$1</strong> de <strong>');
    
    // 2b. Make category names bold in "requires attention" messages
    formattedMessage = formattedMessage.replace(/Une transaction ([A-Za-zÀ-ÿ\s]+) de <strong>([^<]+)<\/strong>/g, 'Une transaction <strong>$1</strong> de <strong>$2</strong>');
    
    // 3. Make caisse names bold (after "pour" or "à")
    formattedMessage = formattedMessage.replace(/(pour|à)\s+([A-Za-z][A-Za-z\s]*[A-Za-z])(?=\s*[.]?\s*(?:a été|et la|suite à|En attente|Veuillez|$|\.))/g, '$1 <strong>$2</strong>');
    
    // 4. Make user names bold (firstName lastName patterns)
    formattedMessage = formattedMessage.replace(/([A-Za-z][A-Za-z\s]{2,})\s+(vous a assigné|a confirmé)/g, '<strong>$1</strong> $2');
    
    // 5. Make user names bold in comments (after "par")
    formattedMessage = formattedMessage.replace(/par ([A-Za-z][A-Za-z\s]{2,})/g, 'par <strong>$1</strong>');
    
    // 6. Make transaction types bold
    formattedMessage = formattedMessage.replace(/(?<!<strong>)(transaction|demande de retrait|Sortie Coffre)(?!<\/strong>)/g, '<strong>$1</strong>');
    
    // 7. Make comment-related terms bold
    formattedMessage = formattedMessage.replace(/(?<!<strong>)(commentaire)(?!<\/strong>)/g, '<strong>$1</strong>');
    
    // 8. Make transaction codes bold (patterns like 25COPBO000037)
    formattedMessage = formattedMessage.replace(/(?<!<strong>)(\d+[A-Z]{2,}\d+)(?!<\/strong>)/g, '<strong>$1</strong>');
    
    // 9. Make "demande de correction" bold and red in correction request notifications
    formattedMessage = formattedMessage.replace(/(?<!<strong>)(demande de correction)(?!<\/strong>)/gi, '<strong style="color: #EF456E;">$1</strong>');
    
    return formattedMessage;
  };

  // Get notification body text
  const getNotificationBody = (notification) => {
    const message = notification.message || 'Nouvelle notification';
    const formatted = parseAndFormatAmounts(message);
    return formatted;
  };

  // Skeleton loading component
  const NotificationSkeleton = () => (
    <Box>
      <Box sx={{ padding: '16px 24px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          {/* Left side skeleton */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Box sx={{ width: '120px', height: '16px', backgroundColor: '#E5E7EB', borderRadius: '4px' }} />
            <Box sx={{ width: '200px', height: '14px', backgroundColor: '#F3F4F6', borderRadius: '4px' }} />
            <Box sx={{ width: '150px', height: '14px', backgroundColor: '#F3F4F6', borderRadius: '4px' }} />
          </Box>
          
          {/* Right side skeleton */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
            <Box sx={{ width: '80px', height: '14px', backgroundColor: '#F3F4F6', borderRadius: '4px' }} />
            <Box sx={{ width: '100px', height: '12px', backgroundColor: '#F3F4F6', borderRadius: '4px' }} />
          </Box>
        </Box>
        
        {/* Body skeleton */}
        <Box sx={{ marginTop: '8px' }}>
          <Box sx={{ width: '100%', height: '14px', backgroundColor: '#F3F4F6', borderRadius: '4px', marginBottom: '4px' }} />
          <Box sx={{ width: '80%', height: '14px', backgroundColor: '#F3F4F6', borderRadius: '4px' }} />
        </Box>
      </Box>
    </Box>
  );

  // Render skeleton notifications
  const renderSkeletonNotifications = () => {
    return Array.from({ length: 3 }, (_, index) => (
      <Box key={`skeleton-${index}`}>
        <NotificationSkeleton />
        {index < 2 && <Divider sx={{ borderColor: '#F7F7F7' }} />}
      </Box>
    ));
  };

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [isOpen]);

  if (!isOpen || !isPositioned) return null;

  const panelContent = (
    <Box
      ref={panelRef}
      className="notification-panel"
      sx={{
        ...panelStyle,
        maxHeight: '600px',
        background: '#FFFFFF',
        boxShadow: '0px 20px 80px #00000011',
        border: '1px solid #F7F7F7',
        opacity: 1,
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '12px'
      }}
    >
      {/* Triangle pointer - dynamically positioned to point to notification button */}
      <Box
        sx={{
          position: 'absolute',
          top: '-12px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1001
        }}
      >
        <svg width="24" height="12" viewBox="0 0 24 12">
          <polygon points="12,0 0,12 24,12" fill="#FFFFFF" stroke="#F7F7F7" strokeWidth="1"/>
        </svg>
      </Box>
      {/* Header */}
      <Box
        sx={{
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #F7F7F7'
        }}
      >
        <Typography
          sx={{
            textAlign: 'left',
            font: 'normal normal 600 16px/22px Montserrat',
            letterSpacing: '0px',
            color: '#272B2F',
            textTransform: 'none',
            opacity: 0.88
          }}
        >
          Notifications
        </Typography>
        
        <Typography
          onClick={handleMarkAllAsRead}
          sx={{
            color: '#027BC6',
            textAlign: 'left',
            font: 'normal normal 600 14px/18px Montserrat',
            letterSpacing: '0px',
            textTransform: 'none',
            opacity: 0.88,
            cursor: 'pointer',
            '&:hover': {
              opacity: 1
            }
          }}
        >
          Marquer tout comme lu
        </Typography>
      </Box>

      {/* Notifications List */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          padding: 0
        }}
      >
        {loading ? (
          renderSkeletonNotifications()
        ) : notifications.length === 0 ? (
          <Box sx={{ padding: '20px', textAlign: 'center' }}>
            <Typography>Aucune notification</Typography>
          </Box>
        ) : (
          notifications.map((notification, index) => {
            const isRead = !!notification.readAt;
            const details = getNotificationDetails(notification);
            const body = getNotificationBody(notification);
            
            return (
              <Box key={notification.id}>
                <Box
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    padding: '16px 24px',
                    cursor: 'pointer',
                    position: 'relative',
                    backgroundColor: isRead ? 'transparent' : '#F8F9FA',
                    '&:hover': {
                      backgroundColor: '#F0F8FF'
                    }
                  }}
                >
                  {/* Unread indicator */}
                  {!isRead && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        backgroundColor: '#027BC6'
                      }}
                    />
                  )}

                  {/* Top row - Actor name and request details */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    marginBottom: '8px',
                    gap: '12px'
                  }}>
                    {/* Left side - Actor name (40%) */}
                    <Box sx={{ 
                      flex: '0 0 40%', 
                      minWidth: 0,
                      maxWidth: '40%'
                    }}>
                      <Typography
                        sx={{
                          textAlign: 'left',
                          font: 'normal normal 600 16px/22px Montserrat',
                          letterSpacing: '0px',
                          color: isRead ? '#9A9A9A' : '#272B2F',
                          textTransform: 'none',
                          opacity: 0.88,
                          marginBottom: '4px',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          hyphens: 'auto',
                          lineHeight: '1.2'
                        }}
                      >
                        {notification.actor?.firstName} {notification.actor?.lastName}
                      </Typography>
                    </Box>

                    {/* Right side - Request details (60%) */}
                    <Box sx={{ 
                      flex: '0 0 60%', 
                      minWidth: 0,
                      maxWidth: '60%'
                    }}>
                      <Typography
                        sx={{
                          color: isRead ? '#9A9A9A' : '#027BC6',
                          textAlign: 'right',
                          opacity: 0.88,
                          marginBottom: '4px',
                          fontSize: '14px',
                          fontWeight: 600,
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          hyphens: 'auto',
                          lineHeight: '1.2'
                        }}
                      >
                        {details.type}: {details.amount}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Second row - Location and timestamp */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <Typography
                      sx={{
                        textAlign: 'left',
                        font: 'normal normal 600 14px/18px Montserrat',
                        letterSpacing: '0px',
                        color: isRead ? '#9A9A9A' : '#272B2F',
                        textTransform: 'none',
                        opacity: 0.88
                      }}
                    >
                      {notification.withdrawalRequest?.Caisse || notification.transaction?.Caisse || 'N/A'}
                    </Typography>
                    
                    <Typography
                      sx={{
                        color: '#9A9A9A',
                        textAlign: 'right',
                        font: 'normal normal 600 14px/18px Montserrat',
                        letterSpacing: '0px',
                        textTransform: 'none',
                        opacity: 0.88
                      }}
                    >
                      {formatDate(notification.sentAt)}
                    </Typography>
                  </Box>

                  {/* Notification body */}
                  <Typography
                    sx={{
                      textAlign: 'left',
                      fontSize: '14px',
                      fontFamily: 'Montserrat',
                      fontWeight: 500,
                      letterSpacing: '0px',
                      color: isRead ? '#9A9A9A' : '#272B2F',
                      textTransform: 'none',
                      opacity: 0.88,
                      marginTop: '8px',
                      lineHeight: '22px',
                      '& strong': {
                        fontWeight: 700,
                        color: isRead ? '#6B7280' : '#1F2937'
                      }
                    }}
                    dangerouslySetInnerHTML={{ __html: body }}
                  />
                </Box>
                
                {/* Separator */}
                {index < notifications.length - 1 && (
                  <Divider sx={{ borderColor: '#F7F7F7' }} />
                )}
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );

  return createPortal(panelContent, document.body);
};

export default NotificationPanel;
